import type { MasteryState, Module } from '@ezishizi/learning-core';

export type PinyinMode = 'always' | 'tap' | 'teach_only';

export interface Profile {
  id: string;
  nickname: string;
  avatar: string;
  levelId: string;
  /**
   * The lesson the child is unlocked up to: this one and every earlier one are playable in the
   * Adventure and in Study. Set at onboarding (a child joining mid-term unlocks what the class has
   * covered), raised by a parent from the dashboard, and advanced automatically when every stop
   * of this lesson is done. Where the child actually is on the trail is derived from progress.
   */
  currentLessonId: string;
  higherChinese: boolean;
  pinyinMode: PinyinMode;
  dailyStarGoal: number;
  /** Parent opt-in: keep this profile's Speak recordings (private, 90-day expiry) so they can be
   * audited and used to retrain the scorer. Off by default; the local store never uses this (it
   * has no server to keep anything on). */
  speechRetention: boolean;
  createdAt: string;
}

/**
 * What a session can be of: a learning-core module, or the app-only `film` (a story film watched
 * from the Cinema, `lib/films.ts`; `lessonId` is the lesson whose completion unlocked it). Kept
 * out of learning-core's `Module` because no runner builds questions for it.
 */
export type SessionModule = Module | 'film';

export interface SessionRecord {
  id: string;
  profileId: string;
  lessonId: string;
  setId: string | null;
  module: SessionModule;
  /** The taught character for a word game on the adventure islands (`module` = `word_*`),
   * null for set and lesson modules. */
  character: string | null;
  score: number;
  maxScore: number;
  points: number;
  stars: number;
  startedAt: string;
  finishedAt: string;
}

export interface AttemptRecord {
  id: string;
  profileId: string;
  sessionId: string;
  exerciseType: string;
  character: string;
  wordId: string | null;
  correct: boolean;
  latencyMs: number | null;
  createdAt: string;
}

export interface DailyStat {
  day: string; // YYYY-MM-DD local
  stars: number;
  points: number;
  minutes: number;
  goalMet: boolean;
}

export interface Streak {
  current: number;
  best: number;
  lastGoalDay: string | null;
}

/** Daily-challenge tracker: one row per profile, advanced by recordChallenge. */
export interface ChallengeStats {
  /** Challenges ever finished. */
  total: number;
  /** Consecutive days with at least one finished challenge, as of `lastDay` (raw; the UI zeroes
   * it when `lastDay` is older than yesterday — see `challengeStreakOn`). */
  current: number;
  best: number;
  /** YYYY-MM-DD (child's local day) of the most recent finished challenge, null if none. */
  lastDay: string | null;
  /** Challenges finished on `lastDay`. */
  lastDayCount: number;
}

export function emptyChallengeStats(): ChallengeStats {
  return { total: 0, current: 0, best: 0, lastDay: null, lastDayCount: 0 };
}

export interface ToneStat {
  tone: 0 | 1 | 2 | 3 | 4;
  attempts: number;
  correct: number;
  /** Other tone (as a string key, e.g. "2") mapped to how many times it was said instead. */
  confusedWith: Record<string, number>;
}

/** Per-tone accuracy for a profile, built server-side from scored Speak attempts. */
export interface ToneProfile {
  attempts: number;
  scored: number;
  tones: ToneStat[];
  recent: Array<{
    character: string;
    expectedTone: number;
    detectedTone: number;
    toneScore: number | null;
    createdAt: string;
  }>;
  updatedAt: string | null;
}

/** One reviewer flag (mirrors workers/api/src/flags/types.ts); see docs/reviewer_flags.md. */
export interface ContentFlag {
  id: string;
  reviewerId: string;
  kind: string;
  contentId: string;
  lessonId: string | null;
  levelId: string | null;
  contentVersion: string | null;
  appBuild: string | null;
  reason: string;
  note: string | null;
  suggestedFix: string | null;
  status: 'open' | 'resolved';
  createdAt: string;
  resolvedAt: string | null;
}

/** One server-side pronunciation assessment (mirrors workers/api/src/learner/types.ts). Never
 * carries the vendor payload or an R2 key — only whether audio was kept (`hasAudio`). */
export interface SpeechAttemptRecord {
  id: string;
  profileId: string;
  sessionId: string | null;
  wordId: string | null;
  text: string;
  pinyin: string | null;
  provider: string;
  outcome: 'pass' | 'retry' | 'wrong' | 'no_speech';
  score: number | null;
  attempt: number;
  syllables: Array<{
    text: string;
    expectedTone?: number;
    detectedTone?: number;
    toneScore?: number;
    pronunciationScore?: number;
  }>;
  engineVersion: string | null;
  hasAudio: boolean;
  label: 'fair' | 'harsh' | 'lenient' | null;
  labeledAt: string | null;
  createdAt: string;
}

/**
 * One activity's (lesson/set/module/character) aggregate over every session ever recorded for it
 * — never capped like `listSessions`, so a child who has played more sessions than a page of that
 * returns doesn't lose credit for their earliest work. Returned by the Worker's
 * `GET /v1/profiles/:id/progress` (workers/api/src/learner) and by `LocalStore.getProgress`
 * (`summariseSessions` below); `lib/progress.ts`'s `toProgressMap` turns rows of this into the
 * `ProgressMap` the path and study screens key their lookups by.
 */
export interface ActivitySummary {
  lessonId: string;
  setId: string | null;
  module: string;
  character: string | null;
  /** Sessions finished for this activity. */
  count: number;
  /** Best star rating across them. */
  bestStars: number;
  /** Best score / maxScore over sessions with maxScore > 0; null when none had any. */
  bestRatio: number | null;
  /** ISO timestamp of the latest finish. */
  lastFinishedAt: string;
}

/** Everything the app persists. Implemented by LocalStore (prototype) and ApiStore (Worker + D1). */
export interface LearnerStore {
  readonly kind: 'local' | 'api';
  isReady(): Promise<boolean>;
  signOut(): Promise<void>;
  listProfiles(): Promise<Profile[]>;
  createProfile(input: Pick<Profile, 'nickname' | 'avatar'> & Partial<Profile>): Promise<Profile>;
  updateProfile(id: string, patch: Partial<Profile>): Promise<Profile>;
  getMastery(profileId: string): Promise<MasteryState[]>;
  upsertMastery(profileId: string, rows: MasteryState[]): Promise<void>;
  saveSession(session: SessionRecord, attempts: AttemptRecord[]): Promise<void>;
  listSessions(profileId: string, limit?: number): Promise<SessionRecord[]>;
  /** The per-activity aggregate described by `ActivitySummary`, over every session ever recorded
   * for this profile — what `lib/progress.ts`'s `loadProgress` builds the path's `ProgressMap`
   * from, instead of `listSessions`' capped, newest-first page. */
  getProgress(profileId: string): Promise<ActivitySummary[]>;
  recordDay(
    profileId: string,
    day: string,
    delta: { stars: number; points: number; minutes: number },
    goal: number,
  ): Promise<DailyStat>;
  getDailyStats(profileId: string, days: number): Promise<DailyStat[]>;
  getStreak(profileId: string): Promise<Streak>;
  recordChallenge(profileId: string, day: string): Promise<ChallengeStats>;
  getChallengeStats(profileId: string): Promise<ChallengeStats>;
  getToneProfile(profileId: string): Promise<ToneProfile>;
  /** Newest-first speech attempts for the parent dashboard/result screen. Empty on the local
   * store (nothing is ever persisted there). */
  listSpeechAttempts(profileId: string, limit?: number): Promise<SpeechAttemptRecord[]>;
  /** A parent's fair/harsh/lenient call on one attempt's verdict. No-op on the local store. */
  labelSpeechAttempt(
    profileId: string,
    attemptId: string,
    label: 'fair' | 'harsh' | 'lenient',
  ): Promise<SpeechAttemptRecord | null>;
  /** The kept clip's bytes, or null when there is none (not kept, not found, or the local
   * store, which never keeps audio). Fetched rather than a bare URL because playback needs the
   * session cookie, which an `<audio src>` can't send cross-origin in dev. */
  fetchSpeechAudio(profileId: string, attemptId: string): Promise<Blob | null>;
  /** Deletes every kept recording for a profile; resolves the number removed (0 on the local
   * store, or when there was nothing to delete). */
  deleteSpeechAudio(profileId: string): Promise<number>;
  /** The signed-in account, including reviewer flag mode (docs/reviewer_flags.md). Null on the
   * local store (nothing to be signed in as). */
  getMe(): Promise<{ id: string; email: string | null; reviewer: boolean } | null>;
  /** Files a reviewer flag. Throws on the local store — reviewer mode requires the API store, and
   * the toggle for it is only ever shown once `getMe()` says `reviewer: true`. */
  createFlag(input: {
    kind: string;
    contentId: string;
    lessonId: string | null;
    levelId: string | null;
    contentVersion: string | null;
    appBuild: string | null;
    reason: string;
    note: string | null;
    suggestedFix: string | null;
  }): Promise<ContentFlag>;
  /** A reviewer's own flags. Empty on the local store. */
  listMyFlags(status?: 'open' | 'resolved' | 'all'): Promise<ContentFlag[]>;
  /** Marks one of the signed-in reviewer's own flags resolved. */
  resolveFlag(id: string): Promise<ContentFlag>;
}

export const todayKey = (d = new Date()) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

export const newId = () =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(16).slice(2)}`;

/**
 * Groups sessions into one row per activity (the lesson/set/module/character a `SessionRecord`
 * names), aggregated across every one of them: how many were finished, the best star rating, the
 * best first-try score ratio, and when the activity was last finished. This is `LocalStore`'s
 * `getProgress`: the shape mirrors the Worker's `GET /v1/profiles/:id/progress` aggregate
 * (workers/api/src/learner), but built from every session in memory rather than a SQL query,
 * since the local store has no page limit to respect in the first place.
 */
export function summariseSessions(sessions: SessionRecord[]): ActivitySummary[] {
  const rows = new Map<string, ActivitySummary>();
  for (const s of sessions) {
    const character = s.character ?? null;
    const key = `${s.lessonId}:${s.setId ?? 'lesson'}:${s.module}:${character ?? ''}`;
    const prev = rows.get(key);
    const ratio = s.maxScore > 0 ? s.score / s.maxScore : null;
    rows.set(key, {
      lessonId: s.lessonId,
      setId: s.setId,
      module: s.module,
      character,
      count: (prev?.count ?? 0) + 1,
      bestStars: Math.max(prev?.bestStars ?? 0, s.stars),
      bestRatio: ratio === null ? (prev?.bestRatio ?? null) : Math.max(prev?.bestRatio ?? 0, ratio),
      lastFinishedAt:
        prev && prev.lastFinishedAt > s.finishedAt ? prev.lastFinishedAt : s.finishedAt,
    });
  }
  return [...rows.values()];
}

/**
 * Yesterday of a 'YYYY-MM-DD' local day, built from its own parts rather than `new Date(day)`:
 * that string parses as UTC midnight, so subtracting a day with a local `setDate` and reading it
 * back through `todayKey` lands two days back everywhere west of UTC — e.g. in America/New_York,
 * yesterday of '2026-09-06' came out '2026-09-04' — instead of one.
 */
export function prevDay(day: string): string {
  const [y, m, d] = day.split('-').map(Number);
  return todayKey(new Date(y!, m! - 1, d! - 1));
}

export function nextStreak(prev: Streak, day: string): Streak {
  if (prev.lastGoalDay === day) return prev;
  const consecutive = prev.lastGoalDay === prevDay(day);
  const current = consecutive ? prev.current + 1 : 1;
  return { current, best: Math.max(prev.best, current), lastGoalDay: day };
}

/**
 * The streak shown to the child: `streak.current` when the goal was met today or yesterday (it is
 * still "live"), 0 once a day has passed without it. `Streak.current` itself only changes on a
 * goal-met day (see `nextStreak`), so a screen that rendered it verbatim would keep showing an old
 * length through however many missed days followed — the same reason `challengeStreakOn` exists
 * below for `ChallengeStats`.
 */
export function streakOn(streak: Streak, day: string): number {
  if (!streak.lastGoalDay) return 0;
  return streak.lastGoalDay === day || streak.lastGoalDay === prevDay(day) ? streak.current : 0;
}

/**
 * Advance the daily-challenge tracker by one finished challenge. Unlike `nextStreak` (which is
 * a no-op once the day's goal is already recorded), every challenge counts: the same day just
 * bumps the day's count and the total, without touching the streak.
 */
export function nextChallengeStats(prev: ChallengeStats, day: string): ChallengeStats {
  const total = prev.total + 1;
  if (prev.lastDay === day) {
    return { ...prev, total, lastDayCount: prev.lastDayCount + 1 };
  }
  const consecutive = prev.lastDay === prevDay(day);
  const current = consecutive ? prev.current + 1 : 1;
  return { total, current, best: Math.max(prev.best, current), lastDay: day, lastDayCount: 1 };
}

/** The streak shown to the child: 0 once a day has passed without a challenge. */
export function challengeStreakOn(stats: ChallengeStats, day: string): number {
  if (!stats.lastDay) return 0;
  return stats.lastDay === day || stats.lastDay === prevDay(day) ? stats.current : 0;
}

/** Challenges finished today, or 0 if the last one was on an earlier day. */
export function challengesOn(stats: ChallengeStats, day: string): number {
  return stats.lastDay === day ? stats.lastDayCount : 0;
}
